import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ExternalLink, Radar as RadarIcon } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import { SectionBand } from "@/components/SectionPrimitives";
import { techLinkFor } from "@/lib/radarLinks";
import { articlePath } from "@/lib/radarPaths";

const CAP_LABELS = {
  advisory: { en: "Advisory & Transformation", fr: "Advisory & Transformation" },
  delivery: { en: "Technology & Delivery", fr: "Technologie & Delivery" },
  ai_build: { en: "AI & Decision Build", fr: "IA & Decision Build" },
};

const ClientDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(false);
  const [radarSignals, setRadarSignals] = useState([]);

  useEffect(() => {
    api.get(`/clients/${slug}`).then((r) => setItem(r.data)).catch(() => setErr(true));
  }, [slug]);

  useEffect(() => {
    if (!item || item.confidentiality === "anonymous") return;
    const q = item.client_name;
    if (!q) return;
    api.get(`/search?q=${encodeURIComponent(q)}&language=${lang}&limit=6`)
      .then((r) => {
        const d = r.data || {};
        const merged = [
          ...(d.news || []).map((x) => ({ ...x, _type: "news",     url: articlePath("news", x) })),
          ...(d.insights || []).map((x) => ({ ...x, _type: "insight", url: articlePath("insights", x) })),
          ...(d.alerts || []).map((x) => ({ ...x, _type: "alert",   url: articlePath("alerts", x) })),
          ...(d.jobs || []).map((x) => ({ ...x, _type: "job",     url: articlePath("jobs", x) })),
        ];
        setRadarSignals(merged.slice(0, 6));
      })
      .catch(() => {});
  }, [item, lang]);

  if (err) return <Navigate to="/clients" replace />;
  if (!item) return <div className="dl-container py-20 text-[#4B5563]">{lang === "fr" ? "Chargement de la référence…" : "Loading case study…"}</div>;
  if (item.confidentiality !== "anonymous" && process.env.REACT_APP_CLIENT_REFERENCES_APPROVED !== "true") return <Navigate to="/clients" replace />;

  const isAnonymous = item.confidentiality === "anonymous";
  const displayName = item.client_name || item.anonymous_descriptor || "Confidential client";
  const seoTitle = `${displayName} - Dataleo`;

  return (
    <article data-testid="client-detail">
      <SeoHead
        lang={lang}
        path={`/clients/${slug}`}
        title={seoTitle}
        description={item.description?.slice(0, 160) || `Dataleo case study - ${item.sector || "Supply Chain"}.`}
        image={!isAnonymous ? item.logo_url : undefined}
      />

      {/* Hero cream with big title + optional editorial photo on the right.
          hero_image_url is admin-managed; falls back to logo-only when
          absent so legacy clients still render cleanly. */}
      <section className="bg-[#F6F3EE] border-b border-[#E6E0D5]">
        <div className="dl-container py-16 sm:py-20">
          <Link to="/clients" className="inline-flex items-center gap-1.5 text-[12px] font-bold-dl uppercase text-[#4B5563] hover:text-[#00A878] tracking-widest">
            ← {lang === "fr" ? "Clients & Références" : "Clients & References"}
          </Link>
          <div className={`mt-8 grid ${item.hero_image_url ? "lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] gap-10 lg:gap-14 items-center" : "md:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end"}`}>
            <div>
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                {item.sector || (lang === "fr" ? "Case study" : "Case study")}
                {isAnonymous && <span className="ml-2 text-[#4B5563]">· {lang === "fr" ? "Anonyme" : "Anonymous"}</span>}
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] text-[#0B1210]">{displayName}</h1>
              {item.mission_type && (
                <p className="font-display italic text-2xl text-[#00A878] mt-5" style={{ fontWeight: 800 }}>{item.mission_type}</p>
              )}
              {!isAnonymous && item.logo_url && item.hero_image_url && (
                <img
                  src={item.logo_url}
                  alt={displayName}
                  width="200"
                  height="80"
                  className="mt-8 max-h-14 w-auto object-contain object-left"
                />
              )}
            </div>
            {item.hero_image_url ? (
              <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden bg-[#0E1F1A]" data-testid="client-hero-photo">
                <img
                  src={item.hero_image_url}
                  alt={displayName}
                  loading="eager"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div aria-hidden="true" className="absolute top-5 left-5 w-12 h-[3px] bg-[#00A878] rounded-full" />
                {item.sector && (
                  <div className="absolute left-5 bottom-5">
                    <span className="inline-block font-mono-dl text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 bg-[#0E1F1A]/80 backdrop-blur-sm border border-white/15 text-white/90 rounded-full">
                      {item.sector}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              !isAnonymous && item.logo_url && (
                <img
                  src={item.logo_url}
                  alt={displayName}
                  width="240"
                  height="120"
                  className="max-h-20 w-auto object-contain bg-white p-3 rounded-xl border border-[#E6E0D5]"
                />
              )
            )}
          </div>
        </div>
      </section>

      {/* Content on cream */}
      <SectionBand tone="cream">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)] gap-12">
          <div className="text-[17px] leading-[1.75] text-[#0B1210] max-w-3xl">
            {item.description && (
              <p className="text-[18px] leading-[1.7]">{item.description}</p>
            )}
            {item.case_study_url && !isAnonymous && (
              <p className="mt-10">
                <a
                  href={item.case_study_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dl-pill dl-pill-dark inline-flex"
                >
                  {lang === "fr" ? "Ouvrir le case study" : "Read the case study"} <ExternalLink size={15} strokeWidth={2.5} />
                </a>
              </p>
            )}
          </div>

          <aside className="space-y-5">
            {item.capabilities?.length > 0 && (
              <div className="bg-white rounded-[20px] border border-[#E6E0D5] p-6">
                <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                  {lang === "fr" ? "Capacités mobilisées" : "Capabilities engaged"}
                </div>
                <ul className="mt-4 space-y-3">
                  {item.capabilities.map((k) => (
                    <li key={k} className="text-[14px] font-bold-dl text-[#0B1210] flex items-center gap-2">
                      <span className="text-[#00A878]">›</span> {CAP_LABELS[k]?.[lang] || k}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {item.technologies?.length > 0 && (
              <div className="bg-white rounded-[20px] border border-[#E6E0D5] p-6">
                <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                  {lang === "fr" ? "Technologies" : "Technologies"}
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {item.technologies.map((t) => (
                    <Link
                      key={t}
                      to={techLinkFor(t).to}
                      data-testid={`client-tech-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-[12px] font-bold-dl text-[#0B1210] border border-[#E6E0D5] bg-white hover:border-[#00A878] hover:text-[#00A878] px-3 py-1.5 rounded-full transition-colors"
                    >
                      {t}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {radarSignals.length > 0 && (
              <div data-testid="client-radar-signals" className="bg-white rounded-[20px] border border-[#E6E0D5] p-6">
                <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878] inline-flex items-center gap-1.5">
                  <RadarIcon size={12} /> {lang === "fr" ? "Signaux Radar" : "Radar signals"}
                </div>
                <ul className="mt-4 space-y-3">
                  {radarSignals.map((s) => (
                    <li key={`${s._type}-${s.id}`}>
                      <Link to={s.url} className="block text-[13.5px] font-bold-dl text-[#0B1210] hover:text-[#00A878] leading-snug transition-colors">
                        <span className="font-mono-dl text-[9px] tracking-widest uppercase text-[#4B5563] mr-1.5">{s._type}</span>
                        {s.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/radar/search?q=${encodeURIComponent(displayName)}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold-dl text-[#4B5563] hover:text-[#00A878] transition-colors"
                >
                  {lang === "fr" ? "Voir tous les signaux" : "See all signals"} <ArrowRight size={12} strokeWidth={2.5} />
                </Link>
              </div>
            )}
            <div className="bg-[#0E1F1A] text-white rounded-[20px] p-6">
              <div className="font-mono-dl text-[11px] tracking-[0.22em] uppercase text-[#00A878]">
                {lang === "fr" ? "Un cas similaire ?" : "A similar case?"}
              </div>
              <p className="mt-4 text-[14px] text-white/70 leading-relaxed">
                {lang === "fr"
                  ? "Discutons de la manière dont Dataleo peut vous accompagner sur un programme équivalent."
                  : "Let's discuss how Dataleo can support you on an equivalent program."}
              </p>
              <Link to="/contact" className="mt-5 dl-pill dl-pill-primary inline-flex">
                {lang === "fr" ? "En parler" : "Talk to us"} <ArrowRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </aside>
        </div>
      </SectionBand>
    </article>
  );
};

export default ClientDetail;
